import { useDeferredValue, useState, useEffect, useMemo } from 'react';
import { Plus, Search, Edit2, Trash2, Lock, Mail, Phone, User as UserIcon, Shield, Eye, EyeOff, Loader2, ChevronUp, ChevronDown, Filter, XCircle, LogIn, KeyRound, LogOut, Unlock } from 'lucide-react';
import { useNavigate } from 'react-router';
import { User } from '../types/user';
import { useAuditLog } from '../hooks/useAuditLog';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import { warehouseService } from '../services/warehouseService';
import { useAppAlert } from '../components/AppAlertProvider';
import { uiMetadataService } from '../services/uiMetadataService';
import type { Role } from '../types/roles';
import { exportRowsToExcel } from '../utils/excelExport';

export function UserManagement() {
  const { logAction } = useAuditLog();
  const { user: currentUser, impersonateUser, logout } = useAuth();
  const { showAlert, showConfirm } = useAppAlert();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState<'password' | 'session' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadataColumns, setMetadataColumns] = useState<Array<{ key: string; label: string; visibleByDefault?: boolean }>>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [showMobileTools, setShowMobileTools] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const deferredFilters = useDeferredValue(filters);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        setLoading(true);
        setError(null);

        const [usersResponse, rolesResponse, metadataResponse, warehousesResponse] = await Promise.all([
          userService.getUsers(),
          roleService.getRoles().catch((error) => {
            console.error('Failed to load roles:', error);
            return [];
          }),
          uiMetadataService.getModuleTable('user').catch((error) => {
            console.error('Failed to load user table metadata:', error);
            return null;
          }),
          warehouseService.getWarehouses().catch(() => ({ data: [] })),
        ]);

        if (cancelled) return;

        const userData = usersResponse.data || usersResponse;
        const columns = Array.isArray((metadataResponse as any)?.data?.columns) ? (metadataResponse as any).data.columns : [];
        const warehouseItems = Array.isArray((warehousesResponse as any)?.data) ? (warehousesResponse as any).data : [];

        setRoles(rolesResponse);
        setMetadataColumns(columns);
        setWarehouses(warehouseItems.map((item: any) => ({ id: String(item.id), name: item.name || item.code || `Depo #${item.id}` })));
        setUsers(Array.isArray(userData) ? userData.map(mapApiUser) : []);
      } catch (err: any) {
        console.error('Failed to initialize user management:', err);
        if (!cancelled) setError('Kullanıcılar yüklenirken hata oluştu');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  const mapApiUser = (u: any): User => ({
    id: u.id?.toString() || u.user_id?.toString() || '',
    username: u.username || u.user_name || '',
    email: u.email || '',
    phone: u.phone || u.telephone || '',
    fullName: u.full_name || u.name || u.username || '',
    role: u.role || 'lojistik-planlama',
    roles: Array.isArray(u.roles) ? u.roles.map(String) : Array.isArray(u.role_ids) ? u.role_ids.map(String) : typeof u.role_ids === 'string' ? JSON.parse(u.role_ids || '[]').map(String) : [u.role || 'lojistik-planlama'],
    roleIds: Array.isArray(u.roles) ? u.roles.map(String) : Array.isArray(u.role_ids) ? u.role_ids.map(String) : typeof u.role_ids === 'string' ? JSON.parse(u.role_ids || '[]').map(String) : [u.role || 'lojistik-planlama'],
    department: u.department_name || u.department || '',
    status: u.status === 1 ? 'active' : u.status === 0 ? 'inactive' : (u.status || 'active'),
    lastLogin: u.last_login_at || null,
    lastLoginPlatform: u.last_login_platform || null,
    createdAt: u.created_at || null,
    updatedAt: u.updated_at || null,
    allowedPickupWarehouseIds: Array.isArray(u.allowed_pickup_warehouse_ids) ? u.allowed_pickup_warehouse_ids.map(String) : typeof u.allowed_pickup_warehouse_ids === 'string' ? JSON.parse(u.allowed_pickup_warehouse_ids || '[]').map(String) : [],
    canViewNonWarehousePickups: Boolean(u.can_view_non_warehouse_pickups),
    isPaymentPreapprovalController: Boolean(u.is_payment_preapproval_controller ?? u.isPaymentPreapprovalController),
    isPaymentFinalApprover: Boolean(u.is_payment_final_approver ?? u.isPaymentFinalApprover),
    canUpdateFreight: Boolean(u.can_update_freight ?? u.canUpdateFreight),
    canPriceMismatchApprove: Boolean(u.can_price_mismatch_approve ?? u.canPriceMismatchApprove),
    canManualDemurrageEdit: Boolean(u.can_manual_demurrage_edit ?? u.canManualDemurrageEdit),
    sendCodeByEmail: Boolean(u.send_code_by_email ?? u.sendCodeByEmail),
    digitalSignatureImageUrl: u.digital_signature_image_url || u.digitalSignatureImageUrl || '',
  });

  const loadRoles = async () => {
    try {
      const loadedRoles = await roleService.getRoles();
      setRoles(loadedRoles);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const loadTableMetadata = async () => {
    try {
      const response: any = await uiMetadataService.getModuleTable('user');
      const columns = Array.isArray(response?.data?.columns) ? response.data.columns : [];
      setMetadataColumns(columns);
    } catch (error) {
      console.error('Failed to load user table metadata:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getUsers();
      const data = response.data || response;
      if (Array.isArray(data)) {
        setUsers(data.map(mapApiUser));
      }
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError('Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const saveUsers = async (updatedUsers: User[]) => {
    setUsers(updatedUsers);
  };

  const handleDelete = async (user: User) => {
    if (user.id === currentUser?.id) {
      showAlert({ tone: 'warning', title: 'İşlem Engellendi', message: 'Kendi hesabınızı silemezsiniz.' });
      return;
    }

    if (!(await showConfirm({ title: 'Kullanıcı Sil', message: `${user.fullName} kullanıcısını silmek istediğinizden emin misiniz?`, confirmText: 'Sil', tone: 'warning' }))) {
      return;
    }

    try {
      await userService.deleteUser(user.id);
      const updatedUsers = users.filter(u => u.id !== user.id);
      saveUsers(updatedUsers);

      logAction.custom({
        category: 'USER',
        action: 'DELETE',
        entity: 'User',
        entityId: user.id,
        entityName: user.fullName,
        description: `Kullanıcı silindi: ${user.username}`,
        severity: 'warning',
      });
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      showAlert({ tone: 'error', title: 'Silme Başarısız', message: 'Kullanıcı silinirken hata oluştu.' });
    }
  };

  const handleToggleStatus = async (user: User) => {
    if (user.id === currentUser?.id) {
      showAlert({ tone: 'warning', title: 'İşlem Engellendi', message: 'Kendi hesabınızın durumunu değiştiremezsiniz.' });
      return;
    }

    const newStatus = user.status === 'active' ? 'inactive' : 'active';

    try {
      await userService.updateUser(user.id, { status: newStatus === 'active' ? 1 : 0 });
      const updatedUsers = users.map(u =>
        u.id === user.id ? { ...u, status: newStatus, updatedAt: new Date().toISOString() } : u
      );
      saveUsers(updatedUsers);

      logAction.custom({
        category: 'USER',
        action: 'STATUS_CHANGE',
        entity: 'User',
        entityId: user.id,
        entityName: user.fullName,
        description: `Kullanıcı durumu değiştirildi: ${user.status} → ${newStatus}`,
        changes: [{ field: 'status', oldValue: user.status, newValue: newStatus }],
        severity: 'info',
      });
    } catch (err: any) {
      console.error('Failed to update user status:', err);
      showAlert({ tone: 'error', title: 'Durum Güncellenemedi', message: 'Kullanıcı durumu güncellenirken hata oluştu.' });
    }
  };

  const roleLabels = useMemo(() => roles.reduce<Record<string, string>>((acc, role) => {
    acc[role.id] = role.name;
    return acc;
  }, {
    admin: 'Sistem Yöneticisi',
  }), [roles]);

  const warehouseNameMap = useMemo(
    () => new Map(warehouses.map((item) => [item.id, item.name])),
    [warehouses]
  );

  const statusLabels: Record<string, { label: string; color: string; bg: string }> = {
    'active': { label: 'Aktif', color: 'text-green-700', bg: 'bg-green-100' },
    'inactive': { label: 'Pasif', color: 'text-gray-700', bg: 'bg-gray-100' },
    'suspended': { label: 'Askıda', color: 'text-red-700', bg: 'bg-red-100' },
  };

  const getUserWarehouseNames = (user: User) => {
    const names = (user.allowedPickupWarehouseIds || []).map((id) => warehouseNameMap.get(id) || id).filter(Boolean);
    if (names.length === 0 && !user.canViewNonWarehousePickups) return ['Tümü'];
    return user.canViewNonWarehousePickups ? [...names, 'Depo Dışı'] : names;
  };

  const getUserRoleNames = (user: User) => Array.from(new Set([user.role, ...(user.roles || [])].filter(Boolean))).map((roleId) => roleLabels[roleId] || roleId);

  const getUserSearchValue = (user: User, key?: string) => {
    if (!key) {
      return [
        user.fullName,
        user.username,
        user.email,
        user.phone,
        user.department,
        statusLabels[user.status]?.label || user.status,
        ...getUserRoleNames(user),
        ...getUserWarehouseNames(user),
      ].join(' ').toLocaleLowerCase('tr-TR');
    }
    if (key === 'role') return getUserRoleNames(user).join(' ').toLocaleLowerCase('tr-TR');
    if (key === 'pickupWarehousePermissions') return getUserWarehouseNames(user).join(' ').toLocaleLowerCase('tr-TR');
    if (key === 'status') return String(statusLabels[user.status]?.label || user.status || '').toLocaleLowerCase('tr-TR');
    if (key === 'fullName') return `${user.fullName} ${user.username}`.toLocaleLowerCase('tr-TR');
    if (key === 'email' || key === 'phone') return `${user.email} ${user.phone}`.toLocaleLowerCase('tr-TR');
    return String((user as any)[key] ?? '').toLocaleLowerCase('tr-TR');
  };

  const getUserGroupKeys = (user: User, key: string) => {
    if (key === 'role') return getUserRoleNames(user).length > 0 ? getUserRoleNames(user) : ['-'];
    if (key === 'department') return [user.department || '-'];
    if (key === 'pickupWarehousePermissions') return getUserWarehouseNames(user).length > 0 ? getUserWarehouseNames(user) : ['-'];
    if (key === 'status') return [statusLabels[user.status]?.label || user.status || '-'];
    return [String((user as any)[key] || '-')];
  };

  const indexedUsers = useMemo(() => users.map((user) => {
    const roleNames = getUserRoleNames(user);
    const warehouseNames = getUserWarehouseNames(user);
    return {
      user,
      roleIds: Array.from(new Set([user.role, ...(user.roles || [])].filter(Boolean))),
      roleNames,
      warehouseNames,
      searchValue: [
        user.fullName,
        user.username,
        user.email,
        user.phone,
        user.department,
        statusLabels[user.status]?.label || user.status,
        ...roleNames,
        ...warehouseNames,
      ].join(' ').toLocaleLowerCase('tr-TR'),
      fieldValues: {
        role: roleNames.join(' ').toLocaleLowerCase('tr-TR'),
        pickupWarehousePermissions: warehouseNames.join(' ').toLocaleLowerCase('tr-TR'),
        status: String(statusLabels[user.status]?.label || user.status || '').toLocaleLowerCase('tr-TR'),
        fullName: `${user.fullName} ${user.username}`.toLocaleLowerCase('tr-TR'),
        email: `${user.email} ${user.phone}`.toLocaleLowerCase('tr-TR'),
        phone: `${user.email} ${user.phone}`.toLocaleLowerCase('tr-TR'),
      } as Record<string, string>,
    };
  }), [users, roleLabels, warehouseNameMap]);

  const filteredUsers = useMemo(() => {
    const normalizedSearchTerm = deferredSearchTerm.trim().toLocaleLowerCase('tr-TR');
    let result = indexedUsers.filter(({ user, roleIds, searchValue, fieldValues }) => {
      const matchesSearch = !normalizedSearchTerm || searchValue.includes(normalizedSearchTerm);
      const matchesRole = filterRole === 'all' || roleIds.includes(filterRole);
      const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
      const matchesColumnFilters = Object.entries(deferredFilters).every(([key, value]) => {
        if (!value) return true;
        const normalizedValue = value.toLocaleLowerCase('tr-TR');
        if (fieldValues[key] != null) return fieldValues[key].includes(normalizedValue);
        return getUserSearchValue(user, key).includes(normalizedValue);
      });
      return matchesSearch && matchesRole && matchesStatus && matchesColumnFilters;
    }).map((item) => item.user);

    if (sortField) {
      result = [...result].sort((a, b) => {
        const aVal = String((a as any)[sortField] ?? '');
        const bVal = String((b as any)[sortField] ?? '');
        const comparison = aVal.localeCompare(bVal, 'tr');
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [indexedUsers, deferredSearchTerm, filterRole, filterStatus, deferredFilters, sortField, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [deferredSearchTerm, filterRole, filterStatus, deferredFilters, sortField, sortOrder, groupBy, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const pageStart = filteredUsers.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, filteredUsers.length);

  const groupedUsers = useMemo(() => {
    if (!groupBy) return { all: paginatedUsers };
    return paginatedUsers.reduce((acc: Record<string, User[]>, item) => {
      for (const key of getUserGroupKeys(item, groupBy)) {
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
      }
      return acc;
    }, {});
  }, [paginatedUsers, groupBy, warehouseNameMap, roleLabels]);

  const handleExportExcel = () => exportRowsToExcel(filteredUsers as any[], `user-management-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'Users' });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const userStats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status === 'active').length,
    inactive: users.filter((u) => u.status === 'inactive').length,
    admin: users.filter((u) => u.role === 'admin').length,
  }), [users]);
  const isAdminUser = useMemo(() => {
    const effectiveRoles = Array.from(new Set([currentUser?.role, ...(currentUser?.roles || [])].filter(Boolean)));
    return effectiveRoles.includes('admin');
  }, [currentUser?.role, currentUser?.roles]);

  const handleImpersonateUser = async (targetUser: User) => {
    if (!(await showConfirm({
      title: 'Kullanıcı Olarak Giriş Yap',
      message: `${targetUser.fullName} hesabına geçilsin mi?`,
      confirmText: 'Giriş Yap',
      tone: 'info',
    }))) {
      return;
    }

    const response = await impersonateUser(targetUser.id);
    if (!response.success) {
      showAlert({ tone: 'error', title: 'İşlem Başarısız', message: response.message || 'Kullanıcı oturumu açılamadı.' });
      return;
    }

    const effectiveRoles = Array.from(new Set([response.user?.role, ...(response.user?.roles || [])].filter(Boolean)));
    navigate(effectiveRoles.includes('internal-driver') || effectiveRoles.includes('external-driver') ? '/driver-mobile-home' : '/');
  };

  const selectedUsers = useMemo(
    () => users.filter((user) => selectedUserIds.includes(String(user.id))),
    [users, selectedUserIds]
  );

  const toggleSelectUser = (userId: string) => {
    setSelectedUserIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]
    );
  };

  const toggleSelectAllPage = () => {
    const pageIds = paginatedUsers.map((user) => String(user.id));
    const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedUserIds.includes(id));
    setSelectedUserIds((current) =>
      allOnPageSelected ? current.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...current, ...pageIds]))
    );
  };

  const handleResetPasswordSms = async () => {
    const selected = selectedUsers;
    if (selected.length === 0) {
      showAlert({ tone: 'warning', title: 'Kullanıcı Seçilmedi', message: 'Lütfen şifresi sıfırlanacak en az bir kullanıcı seçin.' });
      return;
    }

    if (!(await showConfirm({
      title: 'Şifre Sıfırla (SMS)',
      message: `${selected.length} kullanıcının şifresi sıfırlanacak ve yeni geçici şifre telefondaki SMS ile gönderilecek. Onaylıyor musunuz?`,
      confirmText: 'Şifreleri Sıfırla',
      tone: 'warning',
    }))) {
      return;
    }

    try {
      setBulkActionLoading('password');
      const response = await userService.resetPasswordSendSms(selected.map((user) => user.id));

      const smsSent = Number((response as any)?.data?.smsSentCount ?? 0);
      const smsSkipped = Number((response as any)?.data?.smsSkippedCount ?? 0);
      showAlert({
        tone: 'success',
        title: 'Şifreler Sıfırlandı',
        message: `${response.data?.message || 'Şifreler sıfırlandı'} ${smsSent} kullanıcıya SMS gönderildi${smsSkipped > 0 ? `, ${smsSkipped} kullanıcıda telefon eksik` : '.'}`,
      });

      logAction.custom({
        category: 'USER',
        action: 'PASSWORD_RESET_SMS',
        entity: 'User',
        entityId: selected.map((user) => user.id).join(','),
        entityName: selected.map((user) => user.fullName).join(', '),
        description: `Şifre sıfırlama (SMS): ${selected.map((user) => user.username).join(', ')}`,
        severity: 'warning',
      });

      setSelectedUserIds([]);
    } catch (error: any) {
      console.error('Bulk password reset failed:', error);
      showAlert({ tone: 'error', title: 'Şifre Sıfırlanamadı', message: error?.message || 'Şifre sıfırlama sırasında hata oluştu.' });
    } finally {
      setBulkActionLoading(null);
    }
  };

  const handleResetSmsLimit = async () => {
    if (!(await showConfirm({
      title: 'SMS / Giriş Limitini Sıfırla',
      message: 'Telefon numarasını girerek o numara için günlük 10 SMS / giriş doğrulama kodu limitini kaldıracaksınız. Devam etmek için telefon numarasını yazın.',
      confirmText: 'Limit Sıfırla',
      tone: 'warning',
    }))) {
      return;
    }

    const rawPhone = window.prompt('Telefon numarası (örn. 05321234567):');
    if (!rawPhone || !rawPhone.trim()) {
      showAlert({ tone: 'warning', title: 'İptal Edildi', message: 'Telefon numarası girilmedi.' });
      return;
    }

    try {
      setBulkActionLoading('session');
      const response = await userService.resetSmsLimitByPhone(rawPhone.trim());
      setBulkActionLoading(null);

      const matchedUser = (response as any)?.data?.user;
      showAlert({
        tone: 'success',
        title: 'Limit Sıfırlandı',
        message: `${rawPhone.trim()} için günlük SMS / giriş limiti kaldırıldı.${matchedUser ? ` Eşleşen kullanıcı: ${matchedUser.full_name || matchedUser.username}` : ' Herhangi bir kullanıcı kaydıyla eşleşmedi.'}`,
      });

      logAction.custom({
        category: 'USER',
        action: 'RESET_SMS_LIMIT',
        entity: 'User',
        entityId: matchedUser?.id?.toString(),
        entityName: matchedUser?.full_name || matchedUser?.username,
        description: `Telefon için SMS / giriş limiti sıfırlandı: ${rawPhone.trim()}`,
        severity: 'warning',
      });
    } catch (error: any) {
      console.error('Reset SMS limit failed:', error);
      setBulkActionLoading(null);
      showAlert({ tone: 'error', title: 'Limit Sıfırlanamadı', message: error?.message || 'SMS limiti sıfırlanırken hata oluştu.' });
    }
  };

  const handleTerminateSessions = async () => {
    if (!(await showConfirm({
      title: 'Tüm Oturumları Sonlandır',
      message: 'Tüm kullanıcıların açık oturumları sonlandırılacak. Herkes login sayfasına yönlendirilecek. Onaylıyor musunuz?',
      confirmText: 'Oturumları Sonlandır',
      tone: 'danger',
    }))) {
      return;
    }

    try {
      setBulkActionLoading('session');
      await userService.terminateAllSessions();
      setBulkActionLoading(null);

      showAlert({
        tone: 'success',
        title: 'Oturumlar Sonlandırıldı',
        message: 'Tüm kullanıcı oturumları sonlandırıldı. Şimdi güvenlik nedeniyle siz de login sayfasına yönlendirileceksiniz.',
      });

      logAction.custom({
        category: 'USER',
        action: 'TERMINATE_ALL_SESSIONS',
        entity: 'User',
        entityId: currentUser?.id?.toString(),
        entityName: currentUser?.name,
        description: 'Tüm kullanıcı oturumları sonlandırıldı',
        severity: 'warning',
      });

      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 2000);
    } catch (error: any) {
      console.error('Terminate sessions failed:', error);
      setBulkActionLoading(null);
      showAlert({ tone: 'error', title: 'Oturumlar Sonlandırılamadı', message: error?.message || 'Oturumlar sonlandırılırken hata oluştu.' });
    }
  };

  const resolvedColumns = (
    metadataColumns.length > 0
      ? [...metadataColumns.filter((column) => column.visibleByDefault !== false)]
      : [
        { key: 'fullName', label: 'Kullanıcı' },
        { key: 'email', label: 'İletişim' },
        { key: 'role', label: 'Rol' },
        { key: 'status', label: 'Durum' },
      ]
  )
    .concat([
      { key: 'pickupWarehousePermissions', label: 'Yetkili Depolar' },
      { key: 'paymentApprovalFlags', label: 'Odeme Yetkileri' },
      { key: 'demurragePermission', label: 'Bekleme Bedeli' },
      { key: 'lastLogin', label: 'Son Giriş' },
    ])
    .filter((column, index, array) => array.findIndex((item) => item.key === column.key) === index);

  const renderUserColumn = (user: User, key: string) => {
    switch (key) {
      case 'fullName':
        return <div className="flex items-center"><div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><UserIcon className="w-5 h-5 text-blue-600" /></div><div className="ml-4"><div className="text-sm font-medium text-gray-900">{user.fullName}</div><div className="text-sm text-gray-500">@{user.username}</div></div></div>;
      case 'email':
      case 'phone':
        return <div><div className="text-sm text-gray-900 flex items-center gap-1"><Mail className="w-4 h-4 text-gray-400" />{user.email}</div><div className="text-sm text-gray-500 flex items-center gap-1 mt-1"><Phone className="w-4 h-4 text-gray-400" />{user.phone}</div></div>;
      case 'role':
        return <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-gray-400" /><div className="flex flex-wrap items-center gap-1">{Array.from(new Set([user.role, ...(user.roles || [])].filter(Boolean))).map((roleId) => <span key={roleId} className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">{roleLabels[roleId] || roleId}</span>)}{user.department && <span className="text-xs text-gray-500">{user.department}</span>}</div></div>;
      case 'status':
        const statusInfo = statusLabels[user.status] || statusLabels['active'];
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>{statusInfo.label}</span>;
      case 'pickupWarehousePermissions': {
        const names = getUserWarehouseNames(user);
        if (names.length === 0 && !user.canViewNonWarehousePickups) return <span className="text-sm text-gray-400">Tümü</span>;
        const fullText = names.join(', ');
        return (
          <div className="max-w-[260px]" title={fullText}>
            <span className="block truncate text-sm text-gray-700">{fullText}</span>
          </div>
        );
      }
      case 'paymentApprovalFlags': {
        const flags = [
          user.isPaymentPreapprovalController ? 'On Kontrol' : '',
          user.isPaymentFinalApprover ? 'Nihai Onay' : '',
        ].filter(Boolean);
        return <span className="text-sm text-gray-700">{flags.length > 0 ? flags.join(', ') : '-'}</span>;
      }
      case 'demurragePermission':
        return <span className="text-sm text-gray-700">{user.canManualDemurrageEdit ? 'Manuel Giriş İzni' : '-'}</span>;
      case 'lastLogin':
        return <span className="text-sm text-gray-500">{user.lastLogin ? `${new Date(user.lastLogin).toLocaleString('tr-TR')}${user.lastLoginPlatform ? ` (${user.lastLoginPlatform})` : ''}` : '-'}</span>;
       case 'freightUpdatePermission':
         return <div className="w-full space-y-3"><label className="block text-sm font-medium text-gray-700">Navlun Yetkisi</label><label className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"><input type="checkbox" checked={Boolean(formData.canUpdateFreight)} onChange={(e) => setFormData((current) => ({ ...current, canUpdateFreight: e.target.checked }))} /><span>Navlun fiyatını değiştirebilir</span></label></div>;
       default:
        return <span className="text-sm text-gray-900">{String((user as any)[key] || '-')}</span>;
    }
  };

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
            <p className="text-gray-500 mt-1">Sistem kullanıcılarını yönetin</p>
          </div>
        </div>
        <div className="workiom-toolbar-right">
          <button
            type="button"
            onClick={() => void handleResetPasswordSms()}
            disabled={bulkActionLoading !== null || selectedUserIds.length === 0}
            className={`workiom-outline-button ${bulkActionLoading === 'password' ? 'opacity-60 cursor-not-allowed' : ''}`}
            title="Seçili kullanıcıların şifresini sıfırlayıp SMS ile gönder"
          >
            {bulkActionLoading === 'password' ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Şifre Sıfırla (SMS)
            {selectedUserIds.length > 0 && <span className="ml-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">{selectedUserIds.length}</span>}
          </button>
          <button
            type="button"
            onClick={() => void handleResetSmsLimit()}
            disabled={bulkActionLoading !== null}
            className={`workiom-outline-button ${bulkActionLoading === 'session' ? 'opacity-60 cursor-not-allowed' : ''}`}
            title="Belirtilen telefon numarası için günlük SMS / giriş limitini kaldır"
          >
            {bulkActionLoading === 'session' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
            SMS Limitini Sıfırla
          </button>
          <button
            type="button"
            onClick={() => void handleTerminateSessions()}
            disabled={bulkActionLoading !== null}
            className={`text-red-600 border border-red-300 rounded-lg px-4 py-2 text-sm font-medium hover:bg-red-50 transition-colors ${bulkActionLoading === 'session' ? 'opacity-60 cursor-not-allowed' : ''}`}
            title="Tüm kullanıcıların açık oturumlarını sonlandır"
          >
            {bulkActionLoading === 'session' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
            Tüm Oturumları Sonlandır
          </button>
          <button type="button" onClick={handleExportExcel} className="workiom-outline-button">
            Excele Aktar
          </button>
          <button
            onClick={() => {
              setEditingUser(null);
              setIsModalOpen(true);
            }}
            className="workiom-primary-button"
          >
            <Plus className="w-4 h-4" />
            Yeni Kullanıcı
          </button>
        </div>
      </div>

      <div className="workiom-toolbar !justify-start">
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          <button
            type="button"
            onClick={() => setShowMobileTools((current) => !current)}
            className={`workiom-outline-button md:hidden ${showMobileTools ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}
          >
            <Search className="w-4 h-4" /> Ara / Filtre
          </button>
          {/* Search */}
          <div className={`${showMobileTools ? 'block' : 'hidden'} workiom-search md:block`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="outline-none"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className={`${showMobileTools ? 'block' : 'hidden'} rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 md:block`}
          >
            <option value="all">Tüm Roller</option>
            {Object.entries(roleLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`${showMobileTools ? 'block' : 'hidden'} rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 md:block`}
          >
            <option value="all">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="inactive">Pasif</option>
            <option value="suspended">Askıda</option>
          </select>
        </div>
      </div>

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className={`${showMobileTools ? 'flex' : 'hidden'} w-full flex-wrap items-center gap-3 md:flex`}>
          <button onClick={() => setShowFilters((current) => !current)} className={`workiom-outline-button ${showFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
            <Filter className="w-4 h-4" /> Filtrele
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="workiom-outline-button">
              <XCircle className="w-4 h-4" /> Filtreleri Temizle
            </button>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => setShowGrouping((current) => !current)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
              <Filter className="w-4 h-4" /> Gruplandır
            </button>
            {showGrouping && (
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
                <option value="">Gruplama Yok</option>
                <option value="role">Rol</option>
                <option value="status">Durum</option>
                <option value="department">Departman</option>
                <option value="pickupWarehousePermissions">Yetkili Depolar</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 px-6 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-gray-900">{userStats.total}</div>
          <div className="text-sm text-gray-600">Toplam Kullanıcı</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-green-600">
            {userStats.active}
          </div>
          <div className="text-sm text-gray-600">Aktif</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-gray-600">
            {userStats.inactive}
          </div>
          <div className="text-sm text-gray-600">Pasif</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-blue-600">
            {userStats.admin}
          </div>
          <div className="text-sm text-gray-600">Yönetici</div>
        </div>
      </div>

      {/* Loading/Error State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Kullanıcılar yükleniyor...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
          <button onClick={loadUsers} className="text-blue-600 underline mt-2">Tekrar dene</button>
        </div>
      )}

      {/* User List */}
      {!loading && !error && (
      <>
      <div className="workiom-grid-wrap visible-scrollbars mx-6 border border-gray-200 bg-white" style={{ height: 450, overflow: 'auto' }}>
          <table className="workiom-grid-table text-sm">
            <thead>
              <tr>
                <th className="w-10 px-3">
                  <input
                    type="checkbox"
                    checked={paginatedUsers.length > 0 && paginatedUsers.every((user) => selectedUserIds.includes(String(user.id)))}
                    onChange={toggleSelectAllPage}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label="Sayfadaki tüm kullanıcıları seç"
                  />
                </th>
                {resolvedColumns.map((column) => <th key={column.key} className="text-left cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort(column.key)}><div className="flex items-center gap-2">{column.label}{sortField === column.key && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}</div></th>)}
                <th className="text-left">İşlemler</th>
              </tr>
              {showFilters && (
                <tr>
                  <th className="px-3"></th>
                  {resolvedColumns.map((column) => <th key={`filter-${column.key}`}><input value={filters[column.key] || ''} onChange={(e) => setFilters((current) => ({ ...current, [column.key]: e.target.value }))} placeholder={`${column.label} filtre`} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" /></th>)}
                  <th></th>
                </tr>
              )}
            </thead>
            <tbody>
              {Object.entries(groupedUsers).flatMap(([groupKey, groupItems]) => {
                const rows: JSX.Element[] = [];
                if (groupBy) {
                  rows.push(
                    <tr key={`group-${groupKey}`} className="bg-gray-100">
                      <td colSpan={resolvedColumns.length + 2} className="px-3 py-2">
                        <button type="button" onClick={() => setCollapsedGroups((current) => ({ ...current, [groupKey]: !current[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          {collapsedGroups[groupKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                          <span>{groupKey}</span>
                          <span className="text-gray-500">({groupItems.length})</span>
                        </button>
                      </td>
                    </tr>
                  );
                }

                if (!collapsedGroups[groupKey]) {
                  rows.push(...groupItems.map((user) => (
                    <tr key={user.id}>
                      <td className="w-10 px-3">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(String(user.id))}
                          onChange={() => toggleSelectUser(String(user.id))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label={`${user.fullName} seç`}
                        />
                      </td>
                      {resolvedColumns.map((column) => <td key={column.key}>{renderUserColumn(user, column.key)}</td>)}
                      <td>
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <button onClick={() => { setEditingUser(user); setIsModalOpen(true); }} className="text-blue-600 hover:text-blue-900" title="Düzenle">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleToggleStatus(user)} className={user.status === 'active' ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-900'} title={user.status === 'active' ? 'Pasif Yap' : 'Aktif Yap'} disabled={user.id === currentUser?.id}>
                            {user.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDelete(user)} className="text-red-600 hover:text-red-900" title="Sil" disabled={user.id === currentUser?.id}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                          {isAdminUser && user.id !== currentUser?.id && (
                            <button onClick={() => void handleImpersonateUser(user)} className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-900" title="Bu kullanıcı olarak giriş yap">
                              <LogIn className="w-4 h-4" />
                              <span className="hidden xl:inline">Giriş Yap</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )));
                }
                return rows;
              })}
            </tbody>
          </table>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white">
            <UserIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Kullanıcı bulunamadı</p>
          </div>
        )}
      </div>
      <div className="mx-6 mt-3 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 md:flex-row md:items-center md:justify-between">
        <div>
          {filteredUsers.length > 0 ? `${pageStart}-${pageEnd} / ${filteredUsers.length} kullanıcı gösteriliyor` : 'Gösterilecek kullanıcı yok'}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2">
            <span>Sayfa boyutu</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded border border-gray-300 px-2 py-1">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
          <button type="button" onClick={() => setPage(1)} disabled={page === 1} className="rounded border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50">İlk</button>
          <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1} className="rounded border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50">Önceki</button>
          <span className="px-2">Sayfa {page} / {totalPages}</span>
          <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page === totalPages} className="rounded border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50">Sonraki</button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={page === totalPages} className="rounded border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50">Son</button>
        </div>
      </div>
       
      </>
      )}

      {/* User Form Modal */}
      {isModalOpen && (
        <UserFormModal
          user={editingUser}
          roles={roles}
          onClose={() => setIsModalOpen(false)}
          onSave={async (user) => {
            try {
              if (editingUser) {
                await userService.updateUser(user.id, {
                  username: user.username,
                  email: user.email,
                  phone: user.phone,
                  full_name: user.fullName,
                  ...(user.password ? { password: user.password } : {}),
                  role: user.role,
                  roleIds: Array.from(new Set([user.role, ...(user.roles || []), ...(user.roleIds || [])].filter(Boolean))),
                  department: user.department,
                  status: user.status === 'active' ? 1 : 0,
                  allowedPickupWarehouseIds: user.allowedPickupWarehouseIds || [],
                  canViewNonWarehousePickups: user.canViewNonWarehousePickups || false,
                   isPaymentPreapprovalController: user.isPaymentPreapprovalController || false,
                   isPaymentFinalApprover: user.isPaymentFinalApprover || false,
                   canUpdateFreight: user.canUpdateFreight || false,
                   canPriceMismatchApprove: user.canPriceMismatchApprove || false,
                   canManualDemurrageEdit: user.canManualDemurrageEdit || false,
                   sendCodeByEmail: user.sendCodeByEmail || false,
                   digital_signature_image_url: user.digitalSignatureImageUrl || '',
                  });
                
                logAction.custom({
                  category: 'USER',
                  action: 'UPDATE',
                  entity: 'User',
                  entityId: user.id,
                  entityName: user.fullName,
                  description: `Kullanıcı güncellendi: ${user.username}`,
                  severity: 'info',
                });
              } else {
                await userService.createUser({
                  username: user.username,
                  password: user.password,
                  email: user.email,
                  phone: user.phone,
                   full_name: user.fullName,
                   role: user.role,
                   roleIds: Array.from(new Set([user.role, ...(user.roles || []), ...(user.roleIds || [])].filter(Boolean))),
                   department: user.department,
                  status: 1,
                  allowedPickupWarehouseIds: user.allowedPickupWarehouseIds || [],
                  canViewNonWarehousePickups: user.canViewNonWarehousePickups || false,
                   isPaymentPreapprovalController: user.isPaymentPreapprovalController || false,
                   isPaymentFinalApprover: user.isPaymentFinalApprover || false,
                   canUpdateFreight: user.canUpdateFreight || false,
                   canPriceMismatchApprove: user.canPriceMismatchApprove || false,
                   canManualDemurrageEdit: user.canManualDemurrageEdit || false,
                   sendCodeByEmail: user.sendCodeByEmail || false,
                   digital_signature_image_url: user.digitalSignatureImageUrl || '',
                  });
                
                logAction.custom({
                  category: 'USER',
                  action: 'CREATE',
                  entity: 'User',
                  entityId: user.id,
                  entityName: user.fullName,
                  description: `Yeni kullanıcı oluşturuldu: ${user.username}`,
                  severity: 'info',
                });
              }
              await loadUsers();
              setIsModalOpen(false);
            } catch (err: any) {
              console.error('Failed to save user:', err);
              showAlert({ tone: 'error', title: 'Kayıt Başarısız', message: err?.message || 'Kullanıcı kaydedilirken hata oluştu.' });
            }
          }}
        />
      )}
    </div>
  );
}

function UserFormModal({
  user,
  roles,
  onClose,
  onSave,
}: {
  user: User | null;
  roles: Role[];
  onClose: () => void;
  onSave: (user: User) => Promise<void>;
}) {
  const { showAlert } = useAppAlert();
  const [metadataFields, setMetadataFields] = useState<Array<{ key: string; label: string }>>([]);
  const [formData, setFormData] = useState<Partial<User>>(
    user || {
      username: '',
      password: '',
      email: '',
      phone: '',
      fullName: '',
      role: 'lojistik-planlama',
      roles: ['lojistik-planlama'],
      roleIds: ['lojistik-planlama'],
      department: '',
      status: 'active',
      notes: '',
      isPaymentPreapprovalController: false,
      isPaymentFinalApprover: false,
      canUpdateFreight: false,
      canPriceMismatchApprove: false,
      canManualDemurrageEdit: false,
      sendCodeByEmail: false,
      digitalSignatureImageUrl: '',
    }
  );

  const [showPassword, setShowPassword] = useState(false);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [warehouseSearch, setWarehouseSearch] = useState('');

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const response: any = await uiMetadataService.getModuleForm('user');
        const tabs = Array.isArray(response?.data?.tabs) ? response.data.tabs : [];
        const mainTab = tabs.find((tab: any) => tab.key === 'main') || tabs[0];
        const fields = Array.isArray(mainTab?.fields) ? mainTab.fields : [];
        setMetadataFields(fields);
      } catch (error) {
        console.error('Failed to load user metadata:', error);
      }
    };
    loadMetadata();
    warehouseService.getWarehouses().then((response: any) => {
      const items = Array.isArray(response?.data) ? response.data : [];
      setWarehouses(items.map((item: any) => ({ id: String(item.id), name: item.name || item.code || `Depo #${item.id}` })));
    }).catch(() => setWarehouses([]));
  }, []);

  const sortedWarehouses = useMemo(
    () => [...warehouses].sort((a, b) => a.name.localeCompare(b.name, 'tr')),
    [warehouses]
  );

  const filteredWarehouses = useMemo(() => {
    const query = warehouseSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return sortedWarehouses;
    return sortedWarehouses.filter((warehouse) => warehouse.name.toLocaleLowerCase('tr-TR').includes(query));
  }, [sortedWarehouses, warehouseSearch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username || !formData.fullName || !formData.email) {
      showAlert({ tone: 'warning', title: 'Eksik Bilgi', message: 'Lütfen zorunlu alanları doldurun.' });
      return;
    }

    if (!user && !formData.password) {
      showAlert({ tone: 'warning', title: 'Şifre Gerekli', message: 'Lütfen şifre belirleyin.' });
      return;
    }

    onSave(formData as User);
  };

  const resolvedFieldsBase = metadataFields.length > 0 ? metadataFields.map((field) => field.key) : ['fullName', 'username', 'email', 'phone', 'password', 'role', 'department', 'status', 'notes'];
  const resolvedFields = [...resolvedFieldsBase.filter((field, index, array) => array.indexOf(field) === index), 'pickupWarehousePermissions', 'paymentApprovalFlags', 'demurragePermission', 'emailCodeDelivery', 'freightUpdatePermission'];
  const effectiveSelectedRoles = Array.from(new Set([formData.role, ...(formData.roleIds || []), ...(formData.roles || [])].filter(Boolean))) as string[];

  const renderField = (fieldKey: string) => {
    switch (fieldKey) {
      case 'fullName':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad <span className="text-red-500">*</span></label><input type="text" value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required /></div>;
      case 'username':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Adı <span className="text-red-500">*</span></label><input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required /></div>;
      case 'email':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">E-posta <span className="text-red-500">*</span></label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required /></div>;
      case 'phone':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'password':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">Şifre {!user && <span className="text-red-500">*</span>}</label><div className="relative"><input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required={!user} /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button></div></div>;
      case 'role':
        return <div className="space-y-3"><div><label className="block text-sm font-medium text-gray-700 mb-1">Ana Rol</label><select value={formData.role} onChange={(e) => { const nextRole = e.target.value; setFormData((current) => ({ ...current, role: nextRole, roles: Array.from(new Set([nextRole, ...(current.roles || []), ...(current.roleIds || [])].filter(Boolean))), roleIds: Array.from(new Set([nextRole, ...(current.roles || []), ...(current.roleIds || [])].filter(Boolean))) })); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg">{roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}</select></div><div><label className="block text-sm font-medium text-gray-700 mb-2">Ek Roller</label><div className="max-h-48 space-y-2 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3">{roles.map((role) => { const checked = effectiveSelectedRoles.includes(role.id); return <label key={role.id} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={checked} onChange={(e) => setFormData((current) => { const nextRoles = e.target.checked ? Array.from(new Set([...(current.roleIds || current.roles || []), role.id, current.role || ''].filter(Boolean))) : Array.from(new Set([...(current.roleIds || current.roles || [])].filter((item) => item !== role.id))); const primaryRole = current.role === role.id && !e.target.checked ? (nextRoles[0] || '') : (current.role || nextRoles[0] || role.id); const finalRoles = Array.from(new Set([primaryRole, ...nextRoles].filter(Boolean))); return { ...current, role: primaryRole, roles: finalRoles, roleIds: finalRoles }; })} /><span>{role.name}</span></label>; })}</div><div className="mt-2 flex flex-wrap gap-2">{effectiveSelectedRoles.map((roleId) => <span key={roleId} className={`rounded-full px-3 py-1 text-xs font-medium ${roleId === formData.role ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>{roles.find((item) => item.id === roleId)?.name || roleId}{roleId === formData.role ? ' (Ana Rol)' : ''}</span>)}</div></div></div>;
      case 'department':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">Departman</label><input type="text" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'status':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">Durum</label><select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as any })} className="w-full px-3 py-2 border border-gray-300 rounded-lg"><option value="active">Aktif</option><option value="inactive">Pasif</option><option value="suspended">Askıda</option></select></div>;
      case 'notes':
        return <div><label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'pickupWarehousePermissions':
        return <div className="w-full space-y-3"><label className="block text-sm font-medium text-gray-700">Tracking Yükleme Noktası Yetkisi</label><div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3"><div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between"><input type="text" value={warehouseSearch} onChange={(e) => setWarehouseSearch(e.target.value)} placeholder="Depo ara" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm md:max-w-xs" /><div className="flex items-center gap-2"><button type="button" onClick={() => setFormData((current) => ({ ...current, allowedPickupWarehouseIds: sortedWarehouses.map((warehouse) => warehouse.id) }))} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100">Tümünü Seç</button><button type="button" onClick={() => setFormData((current) => ({ ...current, allowedPickupWarehouseIds: [] }))} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100">Temizle</button></div></div><div className="grid max-h-56 grid-cols-1 gap-2 overflow-auto md:grid-cols-2">{filteredWarehouses.map((warehouse) => { const checked = (formData.allowedPickupWarehouseIds || []).includes(warehouse.id); return <label key={warehouse.id} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={checked} onChange={(e) => setFormData((current) => ({ ...current, allowedPickupWarehouseIds: e.target.checked ? [...new Set([...(current.allowedPickupWarehouseIds || []), warehouse.id])] : (current.allowedPickupWarehouseIds || []).filter((item) => item !== warehouse.id) }))} /><span>{warehouse.name}</span></label>; })}{filteredWarehouses.length === 0 && <div className="text-sm text-gray-500">Aramaya uygun depo bulunamadı.</div>}</div></div>{(formData.allowedPickupWarehouseIds || []).length > 0 && <div className="flex flex-wrap gap-2">{(formData.allowedPickupWarehouseIds || []).map((id) => { const name = warehouses.find((item) => item.id === id)?.name || id; return <span key={id} className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">{name}</span>; })}</div>}<label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={Boolean(formData.canViewNonWarehousePickups)} onChange={(e) => setFormData((current) => ({ ...current, canViewNonWarehousePickups: e.target.checked }))} /><span>Depo olmayan yükleme noktalarını da görebilir</span></label></div>;
      case 'paymentApprovalFlags':
        return <div className="w-full space-y-3"><label className="block text-sm font-medium text-gray-700">Odeme Onay Yetkileri</label><div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3"><label className="flex items-center gap-3 text-sm text-gray-700"><input type="checkbox" checked={Boolean(formData.isPaymentPreapprovalController)} onChange={(e) => setFormData((current) => ({ ...current, isPaymentPreapprovalController: e.target.checked }))} /><span>Odeme On Onay Kontrolcusu</span></label><label className="flex items-center gap-3 text-sm text-gray-700"><input type="checkbox" checked={Boolean(formData.isPaymentFinalApprover)} onChange={(e) => setFormData((current) => ({ ...current, isPaymentFinalApprover: e.target.checked }))} /><span>Odeme Nihai Onaylayicisi</span></label><label className="flex items-center gap-3 text-sm text-gray-700"><input type="checkbox" checked={Boolean(formData.canPriceMismatchApprove)} onChange={(e) => setFormData((current) => ({ ...current, canPriceMismatchApprove: e.target.checked }))} /><span>Fiyat Uyumsuzluk Onayi</span></label></div></div>;
      case 'demurragePermission':
        return <div className="w-full space-y-3"><label className="block text-sm font-medium text-gray-700">Bekleme Bedeli Yetkisi</label><div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3"><label className="flex items-center gap-3 text-sm text-gray-700"><input type="checkbox" checked={Boolean(formData.canManualDemurrageEdit)} onChange={(e) => setFormData((current) => ({ ...current, canManualDemurrageEdit: e.target.checked }))} /><span>Bekleme bedeli manuel giriş aç/kapat butonlarını kullanabilir</span></label></div></div>;
      case 'emailCodeDelivery':
        return <div className="w-full space-y-3"><label className="block text-sm font-medium text-gray-700">Giriş Doğrulama Kodu Gönderimi</label><div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3"><label className="flex items-center gap-3 text-sm text-gray-700"><input type="checkbox" checked={Boolean(formData.sendCodeByEmail)} onChange={(e) => setFormData((current) => ({ ...current, sendCodeByEmail: e.target.checked }))} /><span>Mail ile doğrulama kodu gönder</span></label><p className="text-xs text-gray-500">İşaretlenirse bu kullanıcının mobil girişinde doğrulama kodu SMS yerine e-posta adresine gönderilir.</p></div></div>;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {user ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resolvedFields.map((fieldKey) => (
              <div key={fieldKey} className={fieldKey === 'notes' || fieldKey === 'pickupWarehousePermissions' || fieldKey === 'paymentApprovalFlags' || fieldKey === 'demurragePermission' || fieldKey === 'emailCodeDelivery' ? 'md:col-span-2' : ''}>
                {renderField(fieldKey)}
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Dijital İmza Resmi URL</label>
              <input
                type="text"
                value={formData.digitalSignatureImageUrl || ''}
                onChange={(e) => setFormData({ ...formData, digitalSignatureImageUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="/uploads/imza.png veya tam URL"
              />
              {formData.digitalSignatureImageUrl ? (
                <img src={formData.digitalSignatureImageUrl} alt="Dijital imza" className="mt-2 h-16 rounded border border-gray-200 bg-white p-1" />
              ) : null}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {user ? 'Güncelle' : 'Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
